import { FC, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { localizationSelector } from 'store/localizationState';
import { resetPassword, passwordResetSelector } from 'store/passwordResetState';

import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { pushGTMEvent } from 'utils/analyticsUtils';
import { showSuccessToast } from 'utils/toastUtils';

import Loader from 'components/UI/Loader';
import SeoHelmet from 'components/fragments/SeoHelmet';
import Button from 'components/UI/Buttons/Button';
import InputField from 'components/UI/InputField';
import PageHeader from 'components/UI/Page/PageHeader';
import PageImageWrapper from 'components/UI/Page/PageImageWrapper';
import BackButton from 'components/UI/Buttons/BackButton';

import { Form, LoaderGrid } from './styled';

const emailSchema = z.object({
  email: z
    .email({ message: 'input.email_invalid' })
    .min(1, { message: 'input.email_required' })
});

const ResetPasswordPage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const { languageCode } = useAppSelector(localizationSelector);
  const { completed, isLoading } = useAppSelector(passwordResetSelector);

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    resetField
  } = useForm({
    resolver: zodResolver(emailSchema)
  });

  const [email] = useQuery('email');

  // Email query params
  useEffect(() => {
    if (email) {
      setValue('email', email);
    }
  }, [setValue, email]);

  // Show toast when completed
  useEffect(() => {
    if (completed) {
      showSuccessToast(t('resetpassword.email_verification'));
    }
  }, [completed, t]);

  const onSubmit: SubmitHandler<any> = useCallback(
    ({ email }) => {
      pushGTMEvent('passwordResetRequest');
      dispatch(resetPassword(email, languageCode));
    },
    [dispatch, languageCode]
  );

  return (
    <PageImageWrapper>
      <SeoHelmet title="resetpassword.meta_title" />
      <BackButton>{t('productinfo.btn_back')}</BackButton>
      <PageHeader
        title={t('resetpassword.title')}
        subtitle={t('resetpassword.description')}
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          name="email"
          disabled={isLoading}
          description="input.email_label"
          error={errors.email}
          register={register('email')}
          reset={resetField}
        />
        <Button disabled={isLoading} submit fullWidth>
          {t('resetpassword.btn_reset')}
        </Button>
        <LoaderGrid>{isLoading && <Loader size="medium" />}</LoaderGrid>
      </Form>
    </PageImageWrapper>
  );
};

export default ResetPasswordPage;
